import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, Package, ShieldPlus, Truck } from 'lucide-react';
import { Button, Icon, Note, ProductThumb, StatusPill } from '@/components/common';
import type { ProductCase } from '@/types/product';
import { addProtectionNotLive, orderStatus } from '@/utils/orderPresentation';
import { formatDay, formatRupees } from '@/utils/productCases';
import { shipmentPath } from '@/utils/shipment';

/** Plan name without the word "Protection", for compact lines: "1-Year Accidental Damage". */
const shortPlan = (name?: string) => name?.replace(/ Protection$/, '');

/** One order on the Orders tab (Figma "Card/Order"). Status sets the badge, note and actions. */
const OrderCard: React.FC<{ order: ProductCase }> = ({ order }) => {
  const navigate = useNavigate();
  const { tone, label } = orderStatus(order);
  const detailsPath = `/customer/orders/${order.orderId}`;

  const when =
    order.state === 'on_the_way'
      ? order.shipment?.eta
        ? `Arriving ${order.shipment.eta}`
        : `Ordered ${formatDay(order.orderedAt)}`
      : order.deliveredAt
        ? `Delivered ${formatDay(order.deliveredAt)}`
        : `Ordered ${formatDay(order.orderedAt)}`;
  const meta = [
    order.orderLabel,
    formatRupees(order.price),
    order.state === 'protected' && shortPlan(order.plan?.name),
    order.state === 'not_protected' && order.returnWindow?.endsAt && `Return till ${formatDay(order.returnWindow.endsAt).slice(0, 6)}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const ret = order.returnAction;
  const returnButton = (primary: boolean) =>
    ret && (
      <Button
        size="sm"
        variant={primary ? 'primary' : 'secondary'}
        className="tw-flex-1"
        disabled={ret.disabled}
        onClick={() => ret.path && navigate(ret.path)}
      >
        {ret.label}
      </Button>
    );
  const details = (hideOnMobile = false) => (
    <Button size="sm" variant="secondary" className={hideOnMobile ? 'tw-hidden tw-flex-1 md:tw-inline-flex' : 'tw-flex-1'} onClick={() => navigate(detailsPath)}>
      Details
    </Button>
  );

  return (
    <article className="tw-flex tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
      <div className="tw-flex tw-items-center tw-gap-[14px]">
        <ProductThumb src={order.imageUrl} alt={order.title} />
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-[3px]">
          <h3 className="tw-text-[15px] tw-font-semibold tw-leading-5 tw-text-ds-text-primary">
            {/* The title always opens the order, including on mobile where "Details" can be hidden. */}
            <Link to={detailsPath} className="tw-rounded hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-ds-brand">
              {order.title}
            </Link>
          </h3>
          <p className="tw-text-[13px] tw-leading-[18px] tw-text-ds-text-secondary">{[order.merchant, when].filter(Boolean).join(' · ')}</p>
        </div>
        <StatusPill tone={tone}>{label}</StatusPill>
      </div>

      {meta && (
        <p className="tw-flex tw-items-center tw-gap-2 tw-text-[12px] tw-font-medium tw-leading-4 tw-text-ds-text-tertiary">
          <Icon icon={Package} size={14} />
          {meta}
        </p>
      )}

      {order.state === 'not_protected' && order.protectionDeadline && !order.protectionClosedOn && (
        <Note icon={ShieldPlus}>Protection available till {formatDay(order.protectionDeadline)}</Note>
      )}
      {order.protectionClosedOn && (
        <Note icon={AlertCircle} tone="warning">
          Protection window closed on {formatDay(order.protectionClosedOn)}
        </Note>
      )}

      <div className="tw-flex tw-gap-2.5">
        {order.state === 'on_the_way' && (
          <>
            <Button size="sm" leadingIcon={Truck} className="tw-flex-1" onClick={() => navigate(shipmentPath(order.orderId!))}>
              Track
            </Button>
            {details()}
          </>
        )}
        {order.state === 'protected' && (
          <>
            <Button
              size="sm"
              className="tw-flex-1"
              onClick={() => order.plan && navigate(`/customer/policies/${order.plan.policy.id}`, { state: { policy: order.plan.policy } })}
            >
              View plan
            </Button>
            {details()}
          </>
        )}
        {order.state === 'not_protected' && !order.protectionClosedOn && (
          <>
            <Button size="sm" className="tw-flex-1" onClick={addProtectionNotLive}>
              Add protection
            </Button>
            {returnButton(false)}
            {details(Boolean(ret))}
          </>
        )}
        {order.protectionClosedOn && (
          <>
            {returnButton(true)}
            {details()}
          </>
        )}
      </div>
    </article>
  );
};

export default OrderCard;
